'use client';

import {
    A11Y_FILTER_DESCRIBED_BY,
    RATING_LABEL_DISPLAY_KEY,
} from '@/components/SearchResultsFacets/constants';
import {
    StyledList,
    StyledListItem,
} from '@/components/SearchResultsFacets/styles';
import { checkRefinementApplied } from '@/components/SearchResultsFacets/utils';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    FILTER,
    FILTER_REMOVE,
    FILTER_TYPE_REVIEWS,
    FILTER_VAL,
    TRACK_TYPE_MULTI_ATTR,
} from '@/src/components/Analytics/constants';
import { FacetNormalized } from '@/src/components/SearchResultsFacets/types';
import useRefinement from '@/src/hooks/useRefinement';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Checkbox, Rating } from '@nxt-costco-com/forge-components';
import {
    Analytics,
    type ComposableWindowArray,
    type ContentStackEntryDataProps,
    getComposableTextByDevFieldKey,
    useCheckScreen,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

export function RatingFilterView({
    facet,
    facetConfig,
    resultsText,
    searchService,
}: {
    facet: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    resultsText: string;
    searchService: SearchServiceProps;
}) {
    const { isTablet } = useCheckScreen();
    const { handleDoRefine } = useRefinement({ searchService, facetConfig });
    const { queryParams: searchParams } = useQueryParams();

    const ratingText = getKeyValue('a11y.ratingtext', facetConfig);

    const sortedBuckets = [...facet.buckets].sort((a, b) => b.low - a.low);

    return (
        <StyledList>
            {sortedBuckets.map((bucket, index) => {
                const isChecked = checkRefinementApplied({
                    facet,
                    bucket,
                    searchParams,
                    searchService,
                    facetConfig,
                });
                const currentValue = parseInt(bucket.val?.toString());
                const currentValueDisplayText =
                    getComposableTextByDevFieldKey({
                        devFieldKey: RATING_LABEL_DISPLAY_KEY,
                        composableWindow:
                            facetConfig.composable_window as ComposableWindowArray,
                        templateVariables: {
                            ratingMinimum: currentValue?.toString(),
                        },
                    }) || `${currentValue} & Up`;

                return (
                    <StyledListItem
                        style={{
                            display: 'flex',
                            alignContent: 'flex-start',
                        }}
                        key={currentValue ?? index}
                        data-private={bucket?.leads_to_fsa_chdi}
                        data-bi-private={bucket?.leads_to_fsa_chdi}
                    >
                        <Analytics
                            analyticData={{
                                trackedData: [
                                    {
                                        key: isChecked ? FILTER_REMOVE : FILTER,
                                        value: FILTER_TYPE_REVIEWS,
                                    },
                                    {
                                        key: FILTER_VAL,
                                        value: currentValueDisplayText,
                                    },
                                ],
                                trackType: TRACK_TYPE_MULTI_ATTR,
                                doNotTrack: bucket?.leads_to_fsa_chdi,
                            }}
                        >
                            <Checkbox
                                ariaDescribeText={A11Y_FILTER_DESCRIBED_BY}
                                onChange={event =>
                                    handleDoRefine(
                                        facet,
                                        bucket,
                                        event?.target?.checked,
                                    )
                                }
                                isChecked={!!isChecked}
                                // @ts-expect-error this works but needs a type override
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: isTablet
                                                ? 'column'
                                                : 'row',
                                        }}
                                    >
                                        <Rating
                                            value={currentValue}
                                            ariaLabel={ratingText}
                                            displayAvgRatingAsText={false}
                                            size={isTablet ? 'small' : 'medium'}
                                        />
                                        <Box sx={{ marginLeft: '5px' }}>
                                            {currentValueDisplayText}
                                        </Box>
                                        <Box sx={visuallyHidden}>
                                            {`${bucket?.count?.toLocaleString()} ${resultsText}`}
                                        </Box>
                                    </Box>
                                }
                                id={`${facet?.facetKey}-${bucket?.val?.toString()?.split(' ')?.join('+')}`}
                            />
                        </Analytics>

                        <Box aria-hidden>
                            ({bucket?.count?.toLocaleString()})
                        </Box>
                    </StyledListItem>
                );
            })}
        </StyledList>
    );
}
